import { Button, Toaster, Tooltip } from "@pej/kore/components";
import type { QueryClient } from "@tanstack/react-query";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { createRootRouteWithContext, Outlet, useRouter } from "@tanstack/react-router";
import { ThemeProvider } from "next-themes";
import { describeError } from "@/api/describe-error";
import { ApiError } from "@/api/error";
import { useServerRecovery } from "@/api/server-reachable";
import { NotFound } from "@/components/page-states";

interface RouterContext {
    queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
    component: RootComponent,
    notFoundComponent: NotFound,
});

function RootComponent() {
    // Two attributes so one theme flip drives the app's `.dark`-keyed tokens
    // and kore's `data-theme`-keyed brand override in lockstep.
    return (
        <ThemeProvider attribute={["class", "data-theme"]} defaultTheme="system" disableTransitionOnChange>
            <Tooltip.Provider delay={500} closeDelay={100}>
                <Outlet />
                <Toaster richColors position="top-right" />
            </Tooltip.Provider>
        </ThemeProvider>
    );
}

export function RootErrorComponent({ error, reset }: ErrorComponentProps) {
    const router = useRouter();
    const apiError = error instanceof ApiError ? error : null;
    const { title, message, unreachable = false } = describeError(error);
    // Loaders and queries run again, then the boundary renders the page.
    const retry = () => {
        reset();
        void router.invalidate();
    };
    const recovery = useServerRecovery(unreachable, retry);
    return (
        <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
            <h1 className="text-xl font-semibold">{title}</h1>
            <p className="max-w-md text-muted-foreground">{message}</p>
            {unreachable && (
                <p aria-live="polite" className="text-sm text-muted-foreground">
                    {recovery.checking
                        ? "Checking…"
                        : recovery.lastTried
                          ? `Still can't reach it (last tried ${recovery.lastTried.toLocaleTimeString()}). Retrying automatically.`
                          : "Retrying automatically."}
                </p>
            )}
            {apiError?.requestId && <p className="text-xs text-muted-foreground">Request ID: {apiError.requestId}</p>}
            <div className="flex gap-2">
                <Button
                    variant="outline"
                    disabled={recovery.checking}
                    onClick={unreachable ? recovery.checkNow : retry}
                >
                    {recovery.checking ? "Trying again…" : "Try again"}
                </Button>
                {/* Home needs the server too, so it would land back here. */}
                {!unreachable && <Button onClick={() => router.navigate({ to: "/" })}>Go home</Button>}
            </div>
        </div>
    );
}
